import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onMounted, PropType, provide, ref } from "vue";

import {
  LineTypeCode,
  PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  PROVIDE_KEY_UPDATE_GET_OFF_BUS_STOP_ID,
  ShowErrorModal,
  ShowToastMessage,
  UpdateRequestGetOffBusStopId,
} from "@/types";
import ReportReasonsSelector from "@/components/main/ReportReasonsSelector.vue";
import SegmentedControl from "../SegmentedControl.vue";
import { IOnBoardLine } from "@/domain/entities/onBoardLine";
import BusStopList from "@/components/line/BusStopList.vue";
import { IBusLocation } from "@/domain/entities/lineInfo";
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import NavigationBar from "../NavigationBar.vue";
import { useBoardStore } from "@/stores/boardStore";
import { IBusStop } from "@/domain/entities/busStop";
import { useLineStore } from "@/stores/lineStore";
import Modal from "./base/Modal.vue";
import ScrollTopControl from "../ScrollTopControl.vue";
import InlineSvg from "vue-inline-svg";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import isNearestFutureDate from "@/use/useIsNearestFutureDate";
import dayjs from "dayjs";
import getBusStopsByDate from "@/use/useGetBusStopsByDate";
import isDateWithinPeriod from "@/use/useIsDateWithinPeriod";

interface AddFavoriteLineParams {
  getOnBusStop?: IBusStop;
  getOffBusStop?: IBusStop;
}

/** '승하차 정류장' 즐겨찾기 변경 전 주의사항 알림 모달 */

const __default__ = {
  name: "BoardingLineInfoModal",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  onBoardLine: {
    type: Object as PropType<IOnBoardLine>,
    required: true,
  },
  busLocations: {
    type: Array as PropType<IBusLocation[]>,
    default: () => [],
  },
  onBoardFavoriteLine: {
    type: Object as PropType<IFavoriteLine>,
  },
},
  emits: ["onClickCloseBtn", "onClickRefreshButton", "onClickBusStopLocation", "updateFavoriteLine"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;
const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const props = __props;

const emit = __emit;

const scrollWrapper = ref<HTMLElement>();

const emitOnBusStopLocation = (busStop: IBusStop) => {
  emit("onClickBusStopLocation", busStop);
};

const emitUpdateFavoriteLine = (favoriteLine: IFavoriteLine) => {
  emit("updateFavoriteLine", favoriteLine);
};

const busStops = computed(() => {
  if (!props.onBoardFavoriteLine) {
    return [];
  }

  const today = dayjs().format("YYYY-MM-DD HH:mm");
  return props.onBoardFavoriteLine.busStops.filter((busStop) =>
    isDateWithinPeriod(today, busStop.useBeginDtm, busStop.useEndDtm)
  );
});

const title = computed(() => {
  const { lineTypeCode, lineRouteName, lineRouteNo } = props.onBoardLine;
  const departureTimeText =
    lineTypeCode === LineTypeCode.Inside
      ? ""
      : ` ${busStops.value[0]?.departureTime ?? ""}`;

  const lineName = useGetLineRouteName(
    lineTypeCode as LineTypeCode,
    lineRouteName,
    lineRouteNo
  );
  return `${lineName} ${departureTimeText}`;
});

/** '사내'셔틀일 때, [하차벨]은 없음 */
const REPORT_TAB_TITLE = "신고";
const GET_OFF_BELL_TAB_TITLE = "하차벨";

const segmentOptions = computed(() => {
  return props.onBoardFavoriteLine?.lineTypeCode === LineTypeCode.Inside
    ? [REPORT_TAB_TITLE]
    : [GET_OFF_BELL_TAB_TITLE, REPORT_TAB_TITLE];
});

const getSegmentIndex = (title: string) => {
  return segmentOptions.value.indexOf(title);
};

const sholudShowBusStopList = computed(
  () =>
    props.onBoardFavoriteLine?.lineTypeCode !== LineTypeCode.Inside &&
    segmentIndex.value === 0
);

const shouldShowReportReasonsSelector = computed(() => {
  const isInsideShuttle =
    props.onBoardFavoriteLine?.lineTypeCode === LineTypeCode.Inside;

  return isInsideShuttle ? segmentIndex.value === 0 : segmentIndex.value === 1;
});

const boardStore = useBoardStore();
const lineStore = useLineStore();

const lineInfo = lineStore.selectedLineInfoDetail;

const closeModal = () => {
  emit("onClickCloseBtn");
};

// 새로고침 버튼 처리
const refreshButton = ref<HTMLElement>();
const isRotating = ref(false);

const onClickRefreshButton = () => {
  if (isRotating.value) return;
  isRotating.value = true;

  refreshButton.value?.classList.add("rotate");
  setTimeout(() => {
    refreshButton.value?.classList.remove("rotate");
    isRotating.value = false;
  }, 500);

  emit("onClickRefreshButton");
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const segmentIndex = ref(0);
const updateSegmentIndex = (index: number) => {
  segmentIndex.value = index;
};

// 하차 요청하기
const updateReuqestGetOffBusStopId = inject(
  PROVIDE_KEY_UPDATE_GET_OFF_BUS_STOP_ID
) as UpdateRequestGetOffBusStopId;

const requestGetOffBus = async (busStopId: string, activate: boolean) => {
  if (!props.onBoardFavoriteLine?.boardUid) {
    showToastMessage("하차 요청 중 오류가 발생했습니다");
    return;
  }

  const getOffBusResult = await boardStore.requestGetOffBus(
    props.onBoardFavoriteLine.boardUid,
    busStopId,
    activate
  );

  if (getOffBusResult.isSuccess) {
    const successMessage = activate
      ? "하차벨 예약이 완료되었습니다"
      : "하차벨 예약이 취소되었습니다";
    showToastMessage(successMessage);
    const targetBusStopId = activate ? busStopId : undefined;

    // 탑승조회 API에서 '하차요청 정류장Id'가 들어오지만,
    // polling으로 호출 중이기 때문에 하차벨 UI 즉각 반영을 위해 '정류장Id' 업데이트
    updateReuqestGetOffBusStopId(
      props.onBoardFavoriteLine.turnId,
      targetBusStopId
    );
  } else {
    handleError("하차 요청 오류", getOffBusResult);
  }
};

const isBookable = computed(
  () => !!props.onBoardFavoriteLine?.bookIntervals?.length
);

const currentFavoriteLine = computed(() => {
  let favoriteLine;

  if (isBookable.value) {
    favoriteLine = lineStore.getFavoriteLine(
      props.onBoardFavoriteLine?.turnId ?? "",
      props.onBoardFavoriteLine?.getOnBusStop?.id,
      props.onBoardFavoriteLine?.getOffBusStop?.id
    );
  } else {
    favoriteLine = lineStore.getNonBookableFavoriteLine(
      props.onBoardFavoriteLine?.turnId ?? ""
    );
  }
  return favoriteLine;
});

/** '승하차 정류장' 즐겨찾기 버튼 클릭 */
const shouldShowSelectFavoriteBusStopTypeAddModal = ref(false);
const shouldShowSelectFavoriteBusStopTypeChangeModal = ref(false);

const selectFavoriteBusStopTarget = ref<IBusStop>();
const updateSelectFavoriteBusStopTarget = (busStop?: IBusStop) => {
  selectFavoriteBusStopTarget.value = busStop;
};

const toggleShowSelectFavoriteBusStopTypeAddModal = (show: boolean) => {
  shouldShowSelectFavoriteBusStopTypeAddModal.value = show;
};
const toggleShowSelectFavoriteBusStopTypeChangeModal = (show: boolean) => {
  shouldShowSelectFavoriteBusStopTypeChangeModal.value = show;
};

const handleSelectFavoriteBusStopType = async (isGetOn: boolean) => {
  const busStop = selectFavoriteBusStopTarget.value;
  if (!busStop) return;

  if (isGetOn) {
    await addFavoriteLine({
      getOnBusStop: busStop,
      getOffBusStop: currentFavoriteLine.value?.getOffBusStop,
    });
  } else {
    await addFavoriteLine({
      getOnBusStop: currentFavoriteLine.value?.getOnBusStop,
      getOffBusStop: busStop,
    });
  }

  toggleShowSelectFavoriteBusStopTypeAddModal(false);
  toggleShowSelectFavoriteBusStopTypeChangeModal(false);
  updateSelectFavoriteBusStopTarget(undefined);
};

const handleGetOnAndOffStop = async (busStop: IBusStop) => {
  const { getOnBusStop, getOffBusStop } = currentFavoriteLine.value ?? {};

  const hasGetOnFavorite = !!getOnBusStop && getOnBusStop.id === busStop.id;
  const hasGetOffFavorite = !!getOffBusStop && getOffBusStop.id === busStop.id;

  // 이미 승차 or 하차 즐겨찾기인 경우, 모달 없이 바로 제거
  if (hasGetOnFavorite) {
    await addFavoriteLine({
      getOnBusStop: undefined,
      getOffBusStop: getOffBusStop,
    });
    return;
  }
  if (hasGetOffFavorite) {
    await addFavoriteLine({
      getOnBusStop: getOnBusStop,
      getOffBusStop: undefined,
    });
    return;
  }

  updateSelectFavoriteBusStopTarget(busStop);

  // 즐겨찾기가 모두 없는 경우 추가 모달, 일부만 있는 경우 변경 모달
  if (!getOnBusStop && !getOffBusStop) {
    toggleShowSelectFavoriteBusStopTypeAddModal(true);
  } else {
    toggleShowSelectFavoriteBusStopTypeChangeModal(true);
  }
};

/** 승/하차 정류장 즐겨찾기 버튼 클릭 */
const handleClickFavoriteButton = async (busStop: IBusStop) => {
  if (!props.onBoardFavoriteLine) return;

  const { isGetOnStop, isGetOffStop } = busStop;

  // [승차 && 하차]인 정류장
  if (isGetOffStop && isGetOnStop) {
    await handleGetOnAndOffStop(busStop);
    return;
  }

  // 미정차 정류장
  if (!isGetOnStop && !isGetOffStop) return;

  // const { getOffBusStop, getOnBusStop } = currentFavoriteLine.value;
  const favoriteBusStop = isGetOnStop
    ? currentFavoriteLine.value?.getOnBusStop
    : currentFavoriteLine.value?.getOffBusStop;
  const isFavoriteAlready = favoriteBusStop?.id === busStop.id;

  // 이미 다른 승차/하차 정류장이 즐겨찾기된 경우, 즐겨찾기 정류장 변경 모달 표출
  if (favoriteBusStop && busStop.id !== favoriteBusStop.id) {
    updateFavoriteBusStopToChange(busStop);
    toggleShowFavoriteBusStopChangeAlert(true);
    return;
  }

  let params: { getOnBusStop?: IBusStop; getOffBusStop?: IBusStop };
  if (isFavoriteAlready) {
    // 즐겨찾기 삭제
    params = isGetOnStop
      ? {
          getOnBusStop: undefined,
          getOffBusStop: currentFavoriteLine.value?.getOffBusStop,
        }
      : {
          getOnBusStop: currentFavoriteLine.value?.getOnBusStop,
          getOffBusStop: undefined,
        };
  } else {
    // 즐겨찾기 추가
    params = isGetOnStop
      ? {
          getOnBusStop: busStop,
          getOffBusStop: currentFavoriteLine.value?.getOffBusStop,
        }
      : {
          getOnBusStop: currentFavoriteLine.value?.getOnBusStop,
          getOffBusStop: busStop,
        };
  }

  await addFavoriteLine(params);
};

/** 즐겨찾기 정류장 변경 알림 모달 */
const shouldShowFavoriteBusStopChangeAlert = ref(false);
const toggleShowFavoriteBusStopChangeAlert = (show: boolean) => {
  shouldShowFavoriteBusStopChangeAlert.value = show;
};

const handleClickFavoriteBusStopChangeAlertOk = async () => {
  if (!favoriteBusStopToChange.value) return;

  const params = favoriteBusStopToChange.value.isGetOnStop
    ? {
        getOnBusStop: favoriteBusStopToChange.value,
        getOffBusStop: currentFavoriteLine.value?.getOffBusStop,
      }
    : {
        getOnBusStop: currentFavoriteLine.value?.getOnBusStop,
        getOffBusStop: favoriteBusStopToChange.value,
      };

  await addFavoriteLine(params);
  toggleShowFavoriteBusStopChangeAlert(false);
};

const handleClickFavoriteBusStopChangeAlertCancel = () => {
  toggleShowFavoriteBusStopChangeAlert(false);
  updateFavoriteBusStopToChange();
};

/** 즐겨찾기 추가 API */
const favoriteBusStopToChange = ref<IBusStop>();
const updateFavoriteBusStopToChange = (busStop?: IBusStop) => {
  favoriteBusStopToChange.value = busStop;
};

const addFavoriteLine = async ({
  getOnBusStop,
  getOffBusStop,
}: AddFavoriteLineParams) => {
  if (!props.onBoardFavoriteLine) return;

  const newFavoriteLine: IFavoriteLine = {
    ...props.onBoardFavoriteLine,
    getOnBusStop: getOnBusStop!,
    getOffBusStop: getOffBusStop!,
    getOnNotice: !!getOnBusStop,
    getOffNotice: !!getOffBusStop,
  };

  const getOnTargetDate = getOnBusStop
    ? dayjs(getOnBusStop.useBeginDtm).format("YYYY-MM-DD")
    : undefined;
  const getOffTargetDate = getOffBusStop
    ? dayjs(getOffBusStop.useBeginDtm).format("YYYY-MM-DD")
    : undefined;

  const result = await lineStore.addFavoriteLine(
    newFavoriteLine,
    getOnTargetDate,
    getOffTargetDate
  );
  if (result.isSuccess) {
    updateFavoriteBusStopToChange();
    emitUpdateFavoriteLine(newFavoriteLine);
    return;
  }

  const { errorMessage, errorTime, resultCode } = result;
  showErrorModal(
    "승하차 정류장 즐겨찾기 오류",
    errorMessage,
    resultCode,
    errorTime
  );
};

const getLineInfoDetail = async () => {
  const result = await lineStore.getLineInfoDetail(
    props.onBoardLine.lineRouteUid
  );
  if (!result.isSuccess) {
    const { errorMessage, errorTime, resultCode } = result;
    showErrorModal("노선 상세 조회", errorMessage, resultCode, errorTime);
    return;
  }
};

/**
 * 탑승중 모달 진입했을 때,
 * 하차벨이 존재한다면 하차벨 ui 업데이트
 */
const initializeRequestedGetOffBusStop = () => {
  if (!props.onBoardFavoriteLine) return;
  if (props.onBoardFavoriteLine.lineTypeCode === LineTypeCode.Inside) return;

  const { turnId, requestGetOffBusStopId } = props.onBoardFavoriteLine;

  if (requestGetOffBusStopId) {
    updateReuqestGetOffBusStopId(turnId, requestGetOffBusStopId);
  }
};

/**
 * MapOfLineBusStop에서 selectedLineInfoDetail(lineStore)를 필요로함.
 * getLineInfoDetail이 selectedLineInfoDetail을 업데이트함.
 */
const initializeLineInfo = () => {
  if (lineInfo?.id === props.onBoardLine.lineRouteUid) return;
  getLineInfoDetail();
};

onMounted(() => {
  initializeRequestedGetOffBusStop();
  initializeLineInfo();
});

const __returned__ = { showToastMessage, showErrorModal, props, emit, scrollWrapper, emitOnBusStopLocation, emitUpdateFavoriteLine, busStops, title, REPORT_TAB_TITLE, GET_OFF_BELL_TAB_TITLE, segmentOptions, getSegmentIndex, sholudShowBusStopList, shouldShowReportReasonsSelector, boardStore, lineStore, lineInfo, closeModal, refreshButton, isRotating, onClickRefreshButton, handleError, segmentIndex, updateSegmentIndex, updateReuqestGetOffBusStopId, requestGetOffBus, isBookable, currentFavoriteLine, shouldShowSelectFavoriteBusStopTypeAddModal, shouldShowSelectFavoriteBusStopTypeChangeModal, selectFavoriteBusStopTarget, updateSelectFavoriteBusStopTarget, toggleShowSelectFavoriteBusStopTypeAddModal, toggleShowSelectFavoriteBusStopTypeChangeModal, handleSelectFavoriteBusStopType, handleGetOnAndOffStop, handleClickFavoriteButton, shouldShowFavoriteBusStopChangeAlert, toggleShowFavoriteBusStopChangeAlert, handleClickFavoriteBusStopChangeAlertOk, handleClickFavoriteBusStopChangeAlertCancel, favoriteBusStopToChange, updateFavoriteBusStopToChange, addFavoriteLine, getLineInfoDetail, initializeRequestedGetOffBusStop, initializeLineInfo, ReportReasonsSelector, SegmentedControl, BusStopList, NavigationBar, Modal, ScrollTopControl, get InlineSvg() { return InlineSvg } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})